import {
  Alert,
  Button,
  Callout,
  ControlGroup,
  FormGroup,
  NonIdealState,
  Spinner,
  Switch,
  Tab,
  type TabId,
  Tabs,
} from "@blueprintjs/core";
import { ItemMultiSelect, ItemSelect } from "@package/ui-apollo-components";
import {
  type CanvasRect,
  DEFAULT_GRID_SIZE,
  fitRectToCanvas,
  isRectOutOfBounds,
  LayoutCanvasEditor,
  LayoutCanvasPositionInputs,
  LayoutCanvasToolbar,
  MIN_TILE_SIZE,
} from "@package/ui-components";
import {
  GetLayoutListNameIdDocument,
  GetPostListNameIdDocument,
  GetTagListNameIdDocument,
  type ProfileLayoutModel,
  type ProfileLayoutScreenPositionModel,
  type ProfileModel,
  ScreenType,
  Templates,
  useGetLayoutQuery,
} from "@package/ui-gql/index-data-access";
import { produce, type WritableDraft } from "immer";
import { uniq } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { LayoutCreateDrawer } from "../layout/LayoutEditDrawer";
import { PostCreateDrawer } from "../post/PostEditDrawer";
import { TagCreateDrawer } from "../tag/TagEditDrawer";

const EMPTY_POSITION: CanvasRect = { x: 0, y: 0, width: 0, height: 0 };

// ProfileLayoutScreenPositionModel's fields are individually nullable
// (unlike Layout's), so a present-but-partial position still needs
// per-field defaults to satisfy CanvasRect.
function toCanvasRect(
  position?: ProfileLayoutScreenPositionModel | null,
): CanvasRect {
  return {
    x: position?.x ?? 0,
    y: position?.y ?? 0,
    width: position?.width ?? 0,
    height: position?.height ?? 0,
  };
}

// Shared default sizing for a newly-created tile (Region "Add" or the
// Flyout toggle) — a fraction of the canvas, floored at MIN_TILE_SIZE, so
// a new tile always starts as something visible and grabbable rather than
// a 0x0 box.
function createDefaultTilePosition(
  canvasWidth: number,
  canvasHeight: number,
  offset = 0,
): CanvasRect {
  if (!canvasWidth || !canvasHeight) {
    return { ...EMPTY_POSITION };
  }

  const size = Math.max(
    MIN_TILE_SIZE,
    Math.round(Math.min(canvasWidth, canvasHeight) / 3),
  );

  return fitRectToCanvas(
    { x: offset, y: offset, width: size, height: size },
    canvasWidth,
    canvasHeight,
    MIN_TILE_SIZE,
  );
}

type ProfileLayoutPostTagSingleProps = {
  disabled?: boolean;
  layout?: ProfileLayoutModel | null;
  onLayoutChanged: (layout: ProfileLayoutModel) => void;
};

function ProfileLayoutPostTagSingle({
  disabled,
  layout,
  onLayoutChanged,
}: ProfileLayoutPostTagSingleProps) {
  const posts = useMemo<string[]>(
    () => (layout?.item?.posts?.filter((p) => !!p) ?? []) as string[],
    [layout?.item?.posts],
  );
  const tags = useMemo<string[]>(
    () => (layout?.item?.tags?.filter((p) => !!p) ?? []) as string[],
    [layout?.item?.tags],
  );

  return (
    <>
      <FormGroup
        fill
        label="Content"
        disabled={disabled}
        helperText="Content the selected Layout will automatically place"
      >
        <ItemMultiSelect
          activeItemIds={posts}
          createDrawerElement={PostCreateDrawer}
          disabled={disabled}
          document={GetPostListNameIdDocument}
          onItemRemoved={(id) =>
            onLayoutChanged({
              ...layout,
              item: {
                ...layout?.item,
                posts:
                  layout?.item?.posts?.filter((postId) => postId !== id) ?? [],
              },
            })
          }
          onItemSelected={(id) => {
            if (id) {
              onLayoutChanged({
                ...layout,
                item: {
                  ...layout?.item,
                  posts: uniq([...(layout?.item?.posts ?? []), id]),
                },
              });
            }
          }}
          sorted
        />
      </FormGroup>
      <FormGroup
        fill
        label="Tags (All Content with Tags)"
        disabled={disabled}
        helperText="Content the selected Layout will automatically place which are assigned the selected Tags"
      >
        <ItemMultiSelect
          activeItemIds={tags}
          createDrawerElement={TagCreateDrawer}
          disabled={disabled}
          document={GetTagListNameIdDocument}
          onItemRemoved={(id) =>
            onLayoutChanged({
              ...layout,
              item: {
                ...layout?.item,
                tags: layout?.item?.tags?.filter((tagId) => tagId !== id) ?? [],
              },
            })
          }
          onItemSelected={(id) => {
            if (id) {
              onLayoutChanged({
                ...layout,
                item: {
                  ...layout?.item,
                  tags: uniq([...(layout?.item?.tags ?? []), id]),
                },
              });
            }
          }}
          sorted
        />
      </FormGroup>
    </>
  );
}

type ProfileLayoutPostTagMultiProps = {
  disabled?: boolean;
  layout?: ProfileLayoutModel | null;
  onLayoutChanged: (layout: ProfileLayoutModel) => void;
  screens?: number;
};

function ProfileLayoutPostTagMulti({
  disabled,
  layout,
  onLayoutChanged,
}: ProfileLayoutPostTagMultiProps) {
  const { data, error, loading } = useGetLayoutQuery({
    variables: {
      id: layout?.layout ?? "",
    },
    skip: !layout?.layout,
  });
  const total =
    (data?.item?.template === Templates.Custom
      ? data?.item?.custom?.positions?.length
      : data?.item?.tileCount) ?? 1;

  if (loading) {
    return <Spinner />;
  }

  if (error) {
    return <NonIdealState title="Failed Loading Layout" icon="warning-sign" />;
  }

  return (
    <>
      {Array.from({ length: total }).map((_, index) => (
        <FormGroup
          key={index}
          fill
          label={`Layout Position ${index + 1}`}
          disabled={disabled}
        >
          <ControlGroup>
            <FormGroup fill label="Content" disabled={disabled}>
              <ItemMultiSelect
                activeItemIds={
                  (layout?.items?.[index]?.posts as string[]) ?? []
                }
                createDrawerElement={PostCreateDrawer}
                disabled={disabled}
                document={GetPostListNameIdDocument}
                onItemRemoved={(id) => {
                  if (layout) {
                    onLayoutChanged(
                      produce(layout, (draft) => {
                        draft.items = draft.items ?? [];
                        draft.items[index] = draft.items[index] ?? {};
                        draft.items[index].posts =
                          draft.items[index].posts ?? [];
                        draft.items[index].posts = draft.items[
                          index
                        ].posts.filter((p) => p !== id);
                      }),
                    );
                  }
                }}
                onItemSelected={(id) => {
                  if (id && layout) {
                    onLayoutChanged(
                      produce(layout, (draft) => {
                        draft.items = draft.items ?? [];
                        draft.items[index] = draft.items[index] ?? {};
                        draft.items[index].posts =
                          draft.items[index].posts ?? [];
                        draft.items[index].posts = uniq([
                          ...(draft.items[index].posts ?? []),
                          id,
                        ]);
                      }),
                    );
                  }
                }}
                sorted
              />
            </FormGroup>
            <FormGroup fill label="Tags" disabled={disabled}>
              <ItemMultiSelect
                activeItemIds={(layout?.items?.[index]?.tags as string[]) ?? []}
                createDrawerElement={TagCreateDrawer}
                disabled={disabled}
                document={GetTagListNameIdDocument}
                onItemRemoved={(id) => {
                  if (layout) {
                    onLayoutChanged(
                      produce(layout, (draft) => {
                        draft.items = draft.items ?? [];
                        draft.items[index] = draft.items[index] ?? {};
                        draft.items[index].tags = draft.items[index].tags ?? [];
                        draft.items[index].tags = draft.items[
                          index
                        ].tags.filter((p) => p !== id);
                      }),
                    );
                  }
                }}
                onItemSelected={(id) => {
                  if (id && layout) {
                    onLayoutChanged(
                      produce(layout, (draft) => {
                        draft.items = draft.items ?? [];
                        draft.items[index] = draft.items[index] ?? {};
                        draft.items[index].tags = draft.items[index].tags ?? [];
                        draft.items[index].tags = uniq([
                          ...(draft.items[index].tags ?? []),
                          id,
                        ]);
                      }),
                    );
                  }
                }}
                sorted
              />
            </FormGroup>
          </ControlGroup>
        </FormGroup>
      ))}
    </>
  );
}

// The flyout hotspot renders in the SAME absolute coordinate space as the
// Region boxes (packages/ui/ui-wall/.../ScreenFlyout.tsx portals it into the
// shared canvas container, with no offset applied for the owning region) —
// so this canvas is sized to the profile's LED canvas, not the region's box.
type ProfileLayoutFlyoutCanvasProps = {
  disabled?: boolean;
  profile?: ProfileModel;
  layout?: ProfileLayoutModel | null;
  onLayoutChanged: (layout: ProfileLayoutModel) => void;
};

function ProfileLayoutFlyoutCanvas({
  disabled,
  profile,
  layout,
  onLayoutChanged,
}: ProfileLayoutFlyoutCanvasProps) {
  const canvasWidth = profile?.screen?.size?.width ?? 0;
  const canvasHeight = profile?.screen?.size?.height ?? 0;
  const position = toCanvasRect(layout?.flyout?.position);

  const handleChange = useCallback(
    (rect: CanvasRect) => {
      onLayoutChanged({
        ...layout,
        flyout: { ...layout?.flyout, position: rect },
      });
    },
    [layout, onLayoutChanged],
  );

  if (!canvasWidth || !canvasHeight) {
    return (
      <Callout title="Set LED Width/Height above to begin">
        Interactive position uses the canvas size set above.
      </Callout>
    );
  }

  return (
    <>
      <LayoutCanvasEditor
        width={canvasWidth}
        height={canvasHeight}
        positions={[position]}
        disabled={disabled}
        selectedIndex={0}
        minTileSize={MIN_TILE_SIZE}
        gridSize={DEFAULT_GRID_SIZE}
        snapEnabled
        onSelect={() => {}}
        onChange={(_, rect) => handleChange(rect)}
      />
      <div className="mt-3">
        <LayoutCanvasPositionInputs
          index={0}
          rect={position}
          canvasWidth={canvasWidth}
          canvasHeight={canvasHeight}
          minTileSize={MIN_TILE_SIZE}
          disabled={disabled}
          onChange={handleChange}
          onDelete={() =>
            onLayoutChanged({
              ...layout,
              flyout: { ...layout?.flyout, position: undefined },
            })
          }
        />
      </div>
    </>
  );
}

type ProfileLayoutEntryProps = {
  disabled?: boolean;
  profile?: ProfileModel;
  layout?: ProfileLayoutModel | null;
  onLayoutChanged: (position: ProfileLayoutModel) => void;
};

function ProfileLayoutEntry({
  disabled,
  profile,
  layout,
  onLayoutChanged,
}: ProfileLayoutEntryProps) {
  const canvasWidth = profile?.screen?.size?.width ?? 0;
  const canvasHeight = profile?.screen?.size?.height ?? 0;

  return (
    <div className="pl-5 border-l border-solid border-slate-200">
      <FormGroup fill disabled={disabled} label="Layout">
        <ItemSelect
          activeItemId={layout?.layout}
          createDrawerElement={LayoutCreateDrawer}
          disabled={disabled}
          document={GetLayoutListNameIdDocument}
          onItemSelected={(id) =>
            onLayoutChanged({
              ...layout,
              layout: id,
            })
          }
        />
      </FormGroup>
      <FormGroup>
        <Switch
          disabled={disabled}
          label="Set interactive content at specific position"
          checked={!!layout?.flyout?.position}
          onChange={() =>
            onLayoutChanged({
              ...layout,
              flyout: {
                ...layout?.flyout,
                position: layout?.flyout?.position
                  ? undefined
                  : createDefaultTilePosition(canvasWidth, canvasHeight),
              },
            })
          }
        />
      </FormGroup>
      {layout?.flyout?.position && (
        <ProfileLayoutFlyoutCanvas
          disabled={disabled}
          profile={profile}
          layout={layout}
          onLayoutChanged={onLayoutChanged}
        />
      )}
      <FormGroup>
        <Switch
          disabled={disabled}
          label="Assign content at specific layout positions"
          checked={!!layout?.detailed}
          onChange={() =>
            onLayoutChanged({
              ...layout,
              detailed: !layout?.detailed,
            })
          }
        />
      </FormGroup>
      {layout?.detailed ? (
        <ProfileLayoutPostTagMulti
          disabled={disabled}
          layout={layout}
          onLayoutChanged={onLayoutChanged}
        />
      ) : (
        <ProfileLayoutPostTagSingle
          disabled={disabled}
          layout={layout}
          onLayoutChanged={onLayoutChanged}
        />
      )}
    </div>
  );
}

// Regions tile the same LED canvas (packages/ui/ui-wall/.../Screens.tsx
// ScreenCustom renders each as an absolute sibling inside the shared
// screens container), so this canvas is shared across all Region tabs
// rather than owned by any one of them.
type ProfileLayoutRegionsCanvasProps = {
  disabled?: boolean;
  profile?: ProfileModel;
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  onProfileChanged: (
    callback: (draft: WritableDraft<ProfileModel>) => void,
  ) => void;
};

function ProfileLayoutRegionsCanvas({
  disabled,
  profile,
  selectedIndex,
  onSelect,
  onProfileChanged,
}: ProfileLayoutRegionsCanvasProps) {
  const [snapEnabled, setSnapEnabled] = useState<boolean>(true);
  const [gridSize, setGridSize] = useState<number>(DEFAULT_GRID_SIZE);
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);

  const canvasWidth = profile?.screen?.size?.width ?? 0;
  const canvasHeight = profile?.screen?.size?.height ?? 0;
  const positions = useMemo<CanvasRect[]>(
    () =>
      (profile?.layouts ?? []).map((layout) => toCanvasRect(layout?.position)),
    [profile?.layouts],
  );

  const outOfBoundsIndexes = useMemo(
    () =>
      canvasWidth && canvasHeight
        ? positions
            .map((rect, index) =>
              isRectOutOfBounds(rect, canvasWidth, canvasHeight) ? index : -1,
            )
            .filter((index) => index !== -1)
        : [],
    [positions, canvasWidth, canvasHeight],
  );

  const handleChange = useCallback(
    (index: number, rect: CanvasRect) => {
      onProfileChanged((draft) => {
        draft.layouts = draft.layouts ?? [];
        draft.layouts[index] = { ...draft.layouts[index], position: rect };
      });
    },
    [onProfileChanged],
  );

  const handleAdd = useCallback(() => {
    const offset = ((profile?.layouts?.length ?? 0) % 5) * 24;
    const position = createDefaultTilePosition(
      canvasWidth,
      canvasHeight,
      offset,
    );

    onProfileChanged((draft) => {
      draft.layouts = draft.layouts ?? [];
      draft.layouts.push({ detailed: false, position });
    });
    onSelect(profile?.layouts?.length ?? 0);
  }, [
    canvasWidth,
    canvasHeight,
    profile?.layouts?.length,
    onProfileChanged,
    onSelect,
  ]);

  const handleDeleteConfirm = useCallback(() => {
    if (deleteIndex === null) {
      return;
    }

    const remaining = (profile?.layouts?.length ?? 0) - 1;

    onProfileChanged((draft) => {
      draft.layouts = draft.layouts ?? [];
      draft.layouts.splice(deleteIndex, 1);
    });

    // Keep the selection pointing at the same underlying region as the
    // array shifts: a region before it moves down an index, and deleting
    // the selected region itself falls through to whatever slid into its
    // slot (or the new last region, if it was the last one).
    if (selectedIndex !== null && remaining > 0) {
      if (deleteIndex < selectedIndex) {
        onSelect(selectedIndex - 1);
      } else if (deleteIndex === selectedIndex) {
        onSelect(Math.min(selectedIndex, remaining - 1));
      }
    }

    setDeleteIndex(null);
  }, [
    deleteIndex,
    selectedIndex,
    profile?.layouts?.length,
    onProfileChanged,
    onSelect,
  ]);

  const handleFitToCanvas = useCallback(() => {
    onProfileChanged((draft) => {
      draft.layouts = (draft.layouts ?? []).map((layout) => ({
        ...layout,
        position: layout.position
          ? fitRectToCanvas(
              toCanvasRect(layout.position),
              canvasWidth,
              canvasHeight,
              MIN_TILE_SIZE,
            )
          : layout.position,
      }));
    });
  }, [onProfileChanged, canvasWidth, canvasHeight]);

  return (
    <FormGroup label="Region Layout" className="mb-4">
      {canvasWidth > 0 && canvasHeight > 0 ? (
        <>
          {!!outOfBoundsIndexes.length && (
            <Callout
              className="mb-3"
              intent="warning"
              icon="warning-sign"
              title="Some regions extend past the canvas"
            >
              <p>
                Region{outOfBoundsIndexes.length > 1 ? "s" : ""}{" "}
                {outOfBoundsIndexes.map((index) => index + 1).join(", ")}{" "}
                {outOfBoundsIndexes.length > 1 ? "are" : "is"} outside the
                canvas bounds. They still render exactly as configured — nothing
                has been changed automatically.
              </p>
              <Button
                disabled={disabled}
                icon="crop"
                intent="warning"
                minimal
                onClick={handleFitToCanvas}
              >
                Fit regions to canvas
              </Button>
            </Callout>
          )}
          <LayoutCanvasToolbar
            disabled={disabled}
            snapEnabled={snapEnabled}
            gridSize={gridSize}
            onToggleSnap={() => setSnapEnabled((prev) => !prev)}
            onGridSizeChange={setGridSize}
            onAddTile={handleAdd}
          />
          <LayoutCanvasEditor
            width={canvasWidth}
            height={canvasHeight}
            positions={positions}
            disabled={disabled}
            selectedIndex={selectedIndex}
            minTileSize={MIN_TILE_SIZE}
            gridSize={snapEnabled ? gridSize : 0}
            snapEnabled={snapEnabled}
            onSelect={(index) => {
              if (index !== null) {
                onSelect(index);
              }
            }}
            onChange={handleChange}
          />
          {selectedIndex !== null && positions[selectedIndex] && (
            <div className="mt-3">
              <LayoutCanvasPositionInputs
                index={selectedIndex}
                rect={positions[selectedIndex]}
                canvasWidth={canvasWidth}
                canvasHeight={canvasHeight}
                minTileSize={MIN_TILE_SIZE}
                disabled={disabled}
                onChange={(rect) => handleChange(selectedIndex, rect)}
                onDelete={() => setDeleteIndex(selectedIndex)}
              />
            </div>
          )}
        </>
      ) : (
        <Callout title="Set LED Width/Height above to begin">
          Region placement uses the canvas size you set above.
        </Callout>
      )}
      <Alert
        isOpen={deleteIndex !== null}
        icon="trash"
        intent="danger"
        cancelButtonText="Cancel"
        confirmButtonText="Delete"
        canEscapeKeyCancel
        canOutsideClickCancel
        onCancel={() => setDeleteIndex(null)}
        onConfirm={handleDeleteConfirm}
      >
        <p>
          Delete Region {deleteIndex !== null ? deleteIndex + 1 : ""}? Any
          content, tags, or screen assignment tied to this position will shift
          for every region after it.
        </p>
      </Alert>
    </FormGroup>
  );
}

type ProfileLayoutProps = {
  disabled?: boolean;
  profile?: ProfileModel;
  onProfileChanged: (
    callback: (draft: WritableDraft<ProfileModel>) => void,
  ) => void;
};

export function ProfileLayout({
  disabled,
  profile,
  onProfileChanged,
}: ProfileLayoutProps) {
  const [selectedTabId, setSelectedTabId] = useState<TabId>(`screen-${0}`);
  const isCustom = profile?.type === ScreenType.Custom;

  const tabs = useMemo(() => {
    if (isCustom) {
      return profile?.layouts ?? [];
    }

    return Array.from({ length: profile?.screen?.count ?? 1 }).map(
      (_, index) => profile?.layouts?.[index] ?? { detailed: false },
    );
  }, [isCustom, profile?.layouts, profile?.screen?.count]);
  const tabTitle = isCustom ? "Region" : "Screen";

  const selectedIndex = useMemo(() => {
    const match = /^screen-(\d+)$/.exec(String(selectedTabId));
    return match ? Number(match[1]) : null;
  }, [selectedTabId]);

  return (
    <>
      {isCustom && (
        <ProfileLayoutRegionsCanvas
          disabled={disabled}
          profile={profile}
          selectedIndex={selectedIndex}
          onSelect={(index) => setSelectedTabId(`screen-${index}`)}
          onProfileChanged={onProfileChanged}
        />
      )}
      <Tabs
        animate
        large
        selectedTabId={selectedTabId}
        onChange={(tabId) => setSelectedTabId(tabId)}
        vertical
      >
        {tabs.map((layout, index) => (
          <Tab
            key={index}
            className="outline-none w-full"
            disabled={disabled}
            id={`screen-${index}`}
            title={`${tabTitle} ${index + 1}`}
            panel={
              <ProfileLayoutEntry
                disabled={disabled}
                profile={profile}
                layout={layout}
                onLayoutChanged={(layout) => {
                  onProfileChanged((draft) => {
                    draft.layouts = draft.layouts ?? [];
                    draft.layouts[index] = layout;
                  });
                }}
              />
            }
          />
        ))}
      </Tabs>
    </>
  );
}
